<template>
  <div class="container">
    <div class="month-scroll-wrapper">
      <v-sheet :class="`month-list month-list-${componentUuid}`" elevation="2">
        <v-slide-group show-arrows :class="`slide-group-${componentUuid}`" ref="slideGroup">
          <v-slide-group-item v-for="(month, index) in months" :key="month.value">
            <v-btn
              class="month-btn text-transform-none"
              :class="{
                selected: isInSelectedRange(index),
                hovered: isInHoveredRange(index)
              }"
              @click="selectMonth(index)"
              :disabled="
                index < numberOfSelectedMonths - 1 ||
                !!_.find(disabledMonths, (item) => item === month.value)
              "
              @mouseover="hoverMonth(index)"
              @mouseleave="hoverMonth(null)"
              :style="{ '--active-color': props.activeColor ?? 'rgba(0, 123, 255, 0.5)' }"
              :tabindex="index == months.length - 1 ? 0 : undefined"
            >
              {{ month.label }}
            </v-btn>
          </v-slide-group-item>
        </v-slide-group>
      </v-sheet>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getPastNMonths } from "@/utils";
import _ from "lodash";
import moment from "moment";
import { v4 } from "uuid";
import { ref, onMounted, onBeforeUnmount, watch, computed, nextTick } from "vue";
import { VSlideGroup } from "vuetify/components";

interface Month {
  value: string;
  label: string;
}

const props = defineProps<{
  numberOfMonths?: number;
  activeColor?: string;
  scrollPosition?: number;
  disabledMonths?: string[];
}>();

const model = defineModel<string[]>();

const numberOfSelectedMonths = computed(() => props.numberOfMonths || 3);
const disabledMonths = computed(() => props.disabledMonths);
const months = ref<Month[]>([]);
const selectedEndIndex = ref<number | null>(null);
const selectedMonths = ref<string[]>(model.value ?? []);
const hoveredEndIndex = ref<number | null>(null);
const componentUuid = ref<string>(v4());
const isScrollInitialized = ref<boolean>(false);
const latestScrollPosition = ref<number>(0);

const emit = defineEmits<{
  (e: "onScrollChange", value: number): void;
}>();

const generateMonths = () => {
  months.value = _.map(getPastNMonths(24, new Date()), (month) => {
    return {
      value: month,
      label: moment(month, "YYYY-MM").format("MMM YYYY")
    };
  });
};

const getIndexes = () => {
  const endIndex = selectedEndIndex.value!;
  const startIndex = endIndex - numberOfSelectedMonths.value + 1;
  return {
    startIndex,
    endIndex
  };
};

const selectMonth = (index: number) => {
  if (index < numberOfSelectedMonths.value - 1) return;
  selectedEndIndex.value = index;
  const { startIndex, endIndex } = getIndexes();
  selectedMonths.value = _(months.value)
    .map((month) => month.value)
    .filter((_month, i) => i >= startIndex && i <= endIndex)
    .value();
};

const hoverMonth = (index: number | null) => {
  hoveredEndIndex.value = index;
};

const isInSelectedRange = (index: number) => {
  const month = months.value[index];
  return _.includes(selectedMonths.value, month.value);
};

const isInHoveredRange = (index: number) => {
  if (hoveredEndIndex.value === null) return false;
  const hoveredStartIndexValue = hoveredEndIndex.value - numberOfSelectedMonths.value + 1;
  const hoveredEndIndexValue = hoveredEndIndex.value;
  return index >= hoveredStartIndexValue && index <= hoveredEndIndexValue;
};

watch(
  selectedMonths,
  (newVal) => {
    if (newVal.length) {
      model.value = newVal;
    }
  },
  { immediate: true }
);

// Prevent the slide‐group from jumping to the first item on focus
const slideGroup = ref<InstanceType<typeof VSlideGroup> | null>(null);
const preventAutoFocus = (e: FocusEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

onMounted(() => {
  generateMonths();
  // Hook into the slide‐group’s focus events
  nextTick(() => {
    const el = slideGroup.value?.$el as HTMLElement | undefined;
    if (el) el.addEventListener("focusin", preventAutoFocus, true);
  });
});

onBeforeUnmount(() => {
  const el = slideGroup.value?.$el as HTMLElement | undefined;
  if (el) el.removeEventListener("focusin", preventAutoFocus, true);
});

watch(model, (newVal) => {
  if (newVal) {
    selectedMonths.value = newVal;
  }
});

const setScrollLeft = (position: number) => {
  const container = document.querySelector(
    `.month-list-${componentUuid.value} .v-slide-group__container`
  );
  if (container) {
    container.scrollLeft = position;
  }
};

watch(
  [componentUuid, numberOfSelectedMonths],
  ([componentUuid, numberOfSelectedMonths], [, oldNumberOfSelectedMonths]) => {
    if (numberOfSelectedMonths !== oldNumberOfSelectedMonths) {
      isScrollInitialized.value = false;
    }
    const container = document.querySelector(
      `.month-list-${componentUuid} .v-slide-group__container`
    );
    if (container && isScrollInitialized.value === false) {
      isScrollInitialized.value = true;
      setScrollLeft(99999);
    }
  }
);

onMounted(() => {
  const container: HTMLDivElement | null = document.querySelector(
    `.month-list-${componentUuid.value} .v-slide-group__container`
  );
  if (container) {
    container.addEventListener("scroll", (ev: Event) => {
      const el: HTMLDivElement = ev.currentTarget as any;
      emit("onScrollChange", el.scrollLeft);
    });
  }
});

watch(props, (newValue, oldValue) => {
  if (_.isNumber(newValue.scrollPosition) && newValue.scrollPosition > -1) {
    const container = document.querySelector(
      `.month-list-${componentUuid.value} .v-slide-group__container`
    );
    if (container && newValue.scrollPosition !== latestScrollPosition.value) {
      latestScrollPosition.value = newValue.scrollPosition;
      setScrollLeft(newValue.scrollPosition);
    }
  }
});
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.month-scroll-wrapper {
  display: flex;
  align-items: center;
  gap: 5px;
}

.month-list {
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  padding: 10px;
  border-radius: 5px;
  flex-grow: 1;
}

.month-btn {
  margin: 5px;
  width: 80px;
}

.hovered {
  background-color: rgba(0, 123, 255, 0.1) !important;
  color: black !important;
  border-radius: 5px;
}
.selected {
  background-color: var(--active-color) !important;
  color: white !important;
  border-radius: 5px;
}
</style>
